import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core'
import { LocaleCode } from 'src/app/interfaces/locale'
import { GeneralService } from 'src/app/services/general/general.service'
import { LocaleService } from 'src/app/services/locale/locale-service'
import { LogService } from 'src/app/services/log/log.service'
import { ModalService } from 'src/app/services/modal/modal.service'
import { UserService } from 'src/app/services/user/user.service'
import { GoogleAdModalComponent } from '../../modals/google-ad-modal/google-ad-modal.component'
import { AppAdModalComponent } from '../../modals/app-ad-modal/app-ad-modal.component'

const STORAGE_KEY_PREFIX_COUPANG = 'blocked_coupang_ad_'
const STORAGE_KEY_PREFIX_TEMU = 'blocked_temu_ad_'
const STORAGE_KEY_PREFIX_GOOGLE = 'blocked_google_ad_'
const STORAGE_KEY_PREFIX_APP = 'blocked_app_ad_'
const STORAGE_KEY_PREFIX_ALI = 'blocked_ali_ad_'

type AdType = 'coupang' | 'app' | 'amazon' | 'temu' | 'google' | 'ali'
/** 컨텐츠 가리는 쿠팡 광고 */
@Component({
  template: '',
})
export abstract class BlockedCoupangAd implements OnInit, OnDestroy {
  constructor(
    protected generalService: GeneralService,
    protected logService: LogService,
    protected localeService: LocaleService,
    protected userService: UserService,
    protected modalService: ModalService
  ) {}

  visited = false
  closeButtonCountDown = 5
  amazonAdEnUrl = 'https://amzn.to/3wnrVDb'
  amazonAdJaUrl = 'https://amzn.to/4icM41b'
  coupangAdUrl = 'https://link.coupang.com/a/69UhD'
  temuAdUrl = 'https://app.temu.com/m/myo8331ha8p'
  aliAdUrl = 'https://s.click.aliexpress.com/e/_omNjkPv'

  isKo = this.localeService.isKo
  isTaiwan = false
  adType: AdType = 'coupang'
  exp085 = this.logService.exp.exp085 /*광고 토글
  0: 바이럴X, 쿠팡1순위
  1: 바이럴중, 쿠팡2순위
  2: 바이럴피크, 쿠팡X
  */
  exp086 = this.logService.exp.exp086 //광고 순서 실험(0:테무-> 타입스, 1:테무))

  @Input() logParams: Record<string, string | number | undefined> | undefined
  @Input() isHost?: boolean = undefined

  @Output() isVisited = new EventEmitter<boolean>()

  get isLoggedIn(): boolean {
    return this.userService.isLoggedIn
  }

  private showCoupangAdSubscription = this.generalService.getShowCoupangAdSubject().subscribe((showCoupangAd) => {
    this.visited = !showCoupangAd
    this.isVisited.emit(this.visited)
  })

  async ngOnInit() {
    this.init()
    // 바로 emit했을 때 ExpressionChangedAfterItHasBeenCheckedError 발생 가능성 있어서 setTimeout으로 처리 (dev에서만 보이는 에러이긴 함)

    window.addEventListener('message', (event) => {
      if (event?.data?.type === 'adClicked') {
        this.onClickIframe()
      }
    })
    if (!this.visited) {
      this.logService.ad.adImpression({ ...this.logParams, adType: this.adType })
      this.startCloseButtonCountDown()
    }
  }

  ngOnDestroy() {
    window.removeEventListener('message', () => {})
    this.showCoupangAdSubscription.unsubscribe()
  }

  onClickIframe(): void {
    if (!this.visited) {
      this.logService.ad.adBannerClicked({ ...this.logParams, type: 'blocked' })
      this.setVisitHistory('coupang')
    }
  }

  onClickCloseButton(): void {
    this.visited = true
    this.isVisited.emit(this.visited)
    this.setVisitHistory(this.adType)
    this.logService.ad.adCloseButtonClicked(this.logParams)
  }

  onClickButton(): void {
    this.logService.ad.adButtonClicked(this.logParams)
    if (this.adType === 'coupang') {
      this.generalService.openInappBrowser(this.coupangAdUrl)
    }
    if (this.adType === 'amazon') {
      if (this.localeService.locale === LocaleCode.ja) {
        this.generalService.openInappBrowser(this.amazonAdJaUrl)
      } else {
        this.generalService.openInappBrowser(this.amazonAdEnUrl)
      }
    }
    if (this.adType === 'temu') {
      this.generalService.openInappBrowser(this.temuAdUrl)
    }
    if (this.adType === 'google') {
      this.showGoogleAdModal()
    }
    if (this.adType === 'ali') {
      this.generalService.openInappBrowser(this.aliAdUrl)
    }
    if (this.adType === 'app') {
      this.showAppInstallModal()
      return
    }
    this.setVisitHistory(this.adType)
  }

  private async init() {
    this.adType = this.getAdType()
    const countryCode = await this.localeService.getCountry()
    this.isTaiwan = countryCode === 'TW'
    this.logParams = { ...this.logParams, adType: this.adType }
    if (this.localeService.locale === LocaleCode.ko) {
      this.visited = !this.generalService.showCoupangAd
    } else {
      // 임시로 해외에서는 아마존 광고 제거
      this.visited = true
    }

    const emitTimeout = setTimeout(() => {
      this.isVisited.emit(this.visited)
      clearTimeout(emitTimeout)
    })
  }

  private getAdType(): AdType {
    if (this.isKo) {
      return this.getKoAdType()
    }
    return 'amazon'
  }

  private getKoAdType() {
    const experimentMap: Record<number, Record<number, AdType[]>> = {
      0: {
        0: ['coupang', 'temu', 'ali', 'app'],
        1: ['coupang', 'ali', 'temu', 'app'],
      },
      1: {
        0: ['temu', 'coupang', 'ali', 'app'],
        1: ['ali', 'coupang', 'temu', 'app'],
      },
      2: {
        0: ['temu', 'ali', 'app'],
        1: ['ali', 'temu', 'app'],
      },
    }

    const adOrder = experimentMap[this.exp085]?.[this.exp086] || ['app']

    for (let i = 0; i < adOrder.length; i++) {
      const ad = adOrder[i]
      const isLast = i === adOrder.length - 1
      const isIntime = this.isInTime(ad, localStorage.getItem(this.getStoarageKey(ad)))

      if (isLast || !isIntime) {
        /*마지막 순서 광고는 계속 띄워줌 */
        return ad
      }
    }

    return 'app'
  }

  private isInTime(type: AdType, dttm: string | null): boolean {
    if (dttm === null || !Date.parse(dttm)) {
      return false
    }
    const currentTime = new Date().getTime()
    /** 날짜 바뀔 경우 광고 다시 보여주기 */
    if (new Date(currentTime).getDate() !== new Date(dttm).getDate()) {
      localStorage.removeItem(this.getStoarageKey(type))
      return false
    }
    return true
  }

  private getStoarageKey(type: AdType) {
    if (type === 'temu') {
      return STORAGE_KEY_PREFIX_TEMU
    }
    if (type === 'google') {
      return STORAGE_KEY_PREFIX_GOOGLE
    }
    if (type === 'ali') {
      return STORAGE_KEY_PREFIX_ALI
    }
    if (type === 'app') {
      return STORAGE_KEY_PREFIX_APP
    }
    return STORAGE_KEY_PREFIX_COUPANG
  }

  private setVisitHistory(type: AdType): void {
    localStorage.setItem(`${this.getStoarageKey(type)}`, new Date().toISOString())
    this.visited = true
    this.isVisited.emit(true)
  }

  private startCloseButtonCountDown(): void {
    const countDownInterval = setInterval(() => {
      this.closeButtonCountDown--
      if (this.closeButtonCountDown <= 0) {
        clearInterval(countDownInterval)
      }
    }, 1000)
  }

  private async showAppInstallModal() {
    const adModal = await this.modalService.openDialog(AppAdModalComponent, {
      isHost: this.isHost,
    })
    await adModal.present()
    this.setVisitHistory('app')
  }

  private async showGoogleAdModal() {
    const adModal = await this.modalService.createModal({
      component: GoogleAdModalComponent,
      cssClass: ['custom-center-transparent-modal', 'ad-modal'],
      backdropDismiss: false,
    })
    await adModal.present()
  }
}
